import React, { useState } from 'react';
import { ArrowLeftIcon, ArrowRightIcon, CheckIcon, SparklesIcon } from 'lucide-react';
import { trackEvent } from '../utils/tracking';

/* ------------------------------------------------------------------ */
/* QUIZ DATA                                                           */
/* ------------------------------------------------------------------ */

type Phase = 'H' | 'E' | 'R';

/** The four possible result outcomes: a dominant phase, or the neutral GENERAL path. */
type ResultKind = Phase | 'GENERAL';
type PhaseScore = Record<Phase, number>;

/** Declared objection/motivation collected from neutral (non-phase) answers. */
type MotivationTag = 'objecion_constancia' | 'primera_vez';
type Answer = {
  id: string;
  label: string;
  /** The phase this answer signals, or null when the answer is neutral (no phase signal). */
  phase: Phase | null;
  /** Weight applied to `phase` when resolving the result. 0 for neutral answers. */
  weight: number;
  /** Retained for analytics compatibility; the guided-reflection questions do not assign it. */
  motivationTag?: MotivationTag;
};
type Question = {
  id: string;
  prompt: string;
  /** Optional small muted line shown above the question prompt (used on Q3). */
  connectorLabel?: string;
  answers: Answer[];
};

/**
 * GUIDED REFLECTION CONTENT + PHASE SIGNAL
 * Q1 and Q2 are the only questions that carry phase signal — Q3 is a
 * non-scoring reflection and NEVER affects the result phase (weight 0 on every option).
 *   H = Hormonal     Q1 weight 7 (primary signal), Q2 weight 3 (secondary signal)
 *   E = Enzimática   same weighting
 *   R = Restaurativa same weighting
 */
const QUESTIONS: Question[] = [{
  id: 'q1',
  prompt: '¿Qué es lo que más sentís que cambió en vos últimamente?',
  answers: [{
    id: 'q1a1',
    label: 'Mi cuerpo ya no responde como antes, aunque haga lo mismo',
    phase: 'H',
    weight: 7
  }, {
    id: 'q1a2',
    label: 'Mi digestión y mi relación con la comida cambiaron',
    phase: 'E',
    weight: 7
  }, {
    id: 'q1a3',
    label: 'Me cuesta sostener energía y sentirme en equilibrio',
    phase: 'R',
    weight: 7
  }, {
    id: 'q1a4',
    label: 'Un poco de todo',
    phase: null,
    weight: 0
  }]
}, {
  id: 'q2',
  prompt: '¿En qué momento del día lo notás más?',
  answers: [{
    id: 'q2a1',
    label: 'Al despertarme: siento que no recuperé del todo',
    phase: 'H',
    weight: 3
  }, {
    id: 'q2a2',
    label: 'Después de comer: siento pesadez o incomodidad',
    phase: 'E',
    weight: 3
  }, {
    id: 'q2a3',
    label: 'Durante la tarde: me cuesta sostener el ritmo',
    phase: 'R',
    weight: 3
  }, {
    id: 'q2a4',
    label: 'No hay un momento puntual',
    phase: null,
    weight: 0
  }]
}, {
  id: 'q3',
  connectorLabel: 'Para mostrarte el protocolo de la forma que más te sirva',
  prompt: 'Si pudieras recuperar una sola cosa primero, ¿cuál elegirías?',
  answers: [
  // Q3 remains intentionally non-scoring: it does not determine H/E/R.
  {
    id: 'q3a1',
    label: 'Sentirme nuevamente en equilibrio con mi cuerpo',
    phase: null,
    weight: 0
  }, {
    id: 'q3a2',
    label: 'Sentirme más liviana y con mejor digestión',
    phase: null,
    weight: 0
  }, {
    id: 'q3a3',
    label: 'Recuperar energía y una rutina que pueda sostener',
    phase: null,
    weight: 0
  }, {
    id: 'q3a4',
    label: 'Tener un recorrido completo para ordenar todo',
    phase: null,
    weight: 0
  }]
}];

/**
 * PHASE ICONS
 * Simple lettered-circle marks matching the H·E·R phase language used
 * throughout the rest of the funnel (ProtocolSection's "H / E / R" ghost letters).
 */
function PhaseIconH({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true" data-id="element-683" data-mp="5x562rvyu2bi">
      <circle cx="12" cy="12" r="10.5" stroke="currentColor" strokeWidth="1.2" data-id="element-684" data-mp="22rji11q0a9ay" />
      <text x="12" y="16" textAnchor="middle" fontSize="11" fontFamily="serif" fill="currentColor" data-id="element-685" data-mp="27pl1vov9acnh">
        H
      </text>
    </svg>;
}
function PhaseIconE({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true" data-id="element-686" data-mp="3t8da93dzj4ho">
      <circle cx="12" cy="12" r="10.5" stroke="currentColor" strokeWidth="1.2" data-id="element-687" data-mp="b1yxh7rgd0kv" />
      <text x="12" y="16" textAnchor="middle" fontSize="11" fontFamily="serif" fill="currentColor" data-id="element-688" data-mp="azwakwlljnkd">
        E
      </text>
    </svg>;
}
function PhaseIconR({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true" data-id="element-689" data-mp="3ahccujsijh08">
      <circle cx="12" cy="12" r="10.5" stroke="currentColor" strokeWidth="1.2" data-id="element-690" data-mp="3af9ench7ihkf" />
      <text x="12" y="16" textAnchor="middle" fontSize="11" fontFamily="serif" fill="currentColor" data-id="element-691" data-mp="3oabq0f4toz4m">
        R
      </text>
    </svg>;
}

/**
 * GENERAL ICON
 * Neutral mark representing "all three phases together" (three dots,
 * no single letter) — visually distinct from PhaseIconH/E/R.
 */
function PhaseIconGeneral({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true" data-id="element-692" data-mp="2hmnwzh6mrpkg">
      <circle cx="12" cy="12" r="10.5" stroke="currentColor" strokeWidth="1.2" data-id="element-693" data-mp="2me61ugbryaap" />
      <circle cx="8.5" cy="12" r="1.2" fill="currentColor" data-id="element-694" data-mp="27ors374adqus" />
      <circle cx="12" cy="12" r="1.2" fill="currentColor" data-id="element-695" data-mp="27qb8cxpy01wk" />
      <circle cx="15.5" cy="12" r="1.2" fill="currentColor" data-id="element-696" data-mp="2cnbef8msl4no" />
    </svg>;
}

/** Display metadata for every possible result outcome, including the neutral GENERAL path. */
const RESULT_META: Record<ResultKind, {
  name: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
}> = {
  H: {
    name: 'Hormonal',
    icon: PhaseIconH
  },
  E: {
    name: 'Enzimática',
    icon: PhaseIconE
  },
  R: {
    name: 'Restaurativa',
    icon: PhaseIconR
  },
  GENERAL: {
    name: 'H.E.R. completo',
    icon: PhaseIconGeneral
  }
};

/**
 * SCORING — deterministic decision tree, no API calls, no additive Q3 influence.
 * Only Q1 (weight 7, primary signal) and Q2 (weight 3, secondary signal) carry
 * phase signal; Q3 never participates. Audit trail:
 *
 *   1. Q1 has a phase AND Q2 has a phase, and they MATCH   → that phase wins.
 *   2. Q1 has a phase AND Q2 has a phase, but they DIFFER  → compare weighted
 *      scores (Q1=7 vs Q2=3); Q1's phase wins since it's the primary signal.
 *   3. Q1 has a phase AND Q2 is neutral                   → Q1's phase wins.
 *   4. Q1 is neutral ("Un poco de todo") AND Q2 has a phase → Q2's phase
 *      breaks the tie and wins.
 *   5. Q1 is neutral AND Q2 is neutral                     → GENERAL (no
 *      single phase dominates — an explicit fourth result, never a fallback
 *      guess at H/E/R).
 */
function resolveQuizResult(selected: Answer[]): {
  result: ResultKind;
  scores: PhaseScore;
  motivationTags: MotivationTag[];
} {
  const q1 = selected[0];
  const q2 = selected[1];
  const scores: PhaseScore = {
    H: 0,
    E: 0,
    R: 0
  };
  if (q1?.phase) scores[q1.phase] += q1.weight;
  if (q2?.phase) scores[q2.phase] += q2.weight;
  const motivationTags = [q1?.motivationTag, q2?.motivationTag].filter((tag): tag is MotivationTag => Boolean(tag));
  let result: ResultKind;
  if (q1?.phase && q2?.phase) {
    if (q1.phase === q2.phase) {
      // Case 1: both questions point to the same phase.
      result = q1.phase;
    } else {
      // Case 2: they disagree — resolve via weighted scores (Q1's 7 beats Q2's 3).
      const order: Phase[] = ['H', 'E', 'R'];
      result = order.reduce<Phase>((best, current) => scores[current] > scores[best] ? current : best, order[0]);
    }
  } else if (q1?.phase && !q2?.phase) {
    // Case 3: Q1 has a phase and Q2 is neutral.
    result = q1.phase;
  } else if (!q1?.phase && q2?.phase) {
    // Case 4: Q1 was "Un poco de todo" (neutral), Q2 breaks the tie.
    result = q2.phase;
  } else {
    // Case 5: both neutral — explicit GENERAL outcome, not a guess.
    result = 'GENERAL';
  }
  return {
    result,
    scores,
    motivationTags
  };
}

/* ------------------------------------------------------------------ */
/* COMPONENT                                                           */
/* ------------------------------------------------------------------ */

type Step = 'intro' | 0 | 1 | 2 | 'result';
export function QuizSection() {
  const [step, setStep] = useState<Step>('intro');
  const [selected, setSelected] = useState<Answer[]>([]);
  const handleStart = () => {
    trackEvent('quiz_start', {
      quiz: 'her_fit'
    });
    setSelected([]);
    setStep(0);
  };
  const handleAnswer = (questionIndex: number, answer: Answer) => {
    trackEvent('quiz_answer', {
      question_number: questionIndex + 1,
      question: QUESTIONS[questionIndex].prompt,
      answer: answer.label,
      motivation_tag: answer.motivationTag ?? null
    });
    const next = [...selected.slice(0, questionIndex), answer];
    setSelected(next);
    if (questionIndex < QUESTIONS.length - 1) {
      setStep(questionIndex + 1 as Step);
      return;
    }
    const {
      result,
      scores,
      motivationTags
    } = resolveQuizResult(next);
    trackEvent('quiz_completed', {
      answers: next.map(a => a.label)
    });
    trackEvent('quiz_result', {
      phase: result,
      phase_name: RESULT_META[result].name,
      // Content-relevance framing shown to the user, kept in the payload for QA/reporting.
      // Sourced from the same constants ResultCard renders so they can never drift apart.
      phase_sentence: result === 'GENERAL' ? GENERAL_PERSONALIZATION_SENTENCE : RESULT_PERSONALIZATION_SENTENCE,
      scores,
      motivation_tags: motivationTags
    });
    setStep('result');
  };
  const handleBack = (questionIndex: number) => {
    setStep(questionIndex - 1 as Step);
  };
  const handleRetake = () => {
    trackEvent('quiz_start', {
      quiz: 'her_fit',
      retake: true
    });
    setSelected([]);
    setStep(0);
  };
  const handleResultCta = (result: ResultKind) => {
    trackEvent('cta_click_post_quiz', {
      phase: result,
      phase_name: RESULT_META[result].name,
      entry_point: 'her_fit_result'
    });
    document.getElementById('offer')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return;
}

/* ------------------------------------------------------------------ */
/* INTRO                                                               */
/* ------------------------------------------------------------------ */

function IntroCard({
  onStart
}: {
  onStart: () => void;
}) {
  return;
}

/* ------------------------------------------------------------------ */
/* QUESTIONS                                                           */
/* ------------------------------------------------------------------ */

type QuestionCardProps = {
  index: number;
  total: number;
  question: Question;
  selectedId?: string;
  onAnswer: (index: number, answer: Answer) => void;
  onBack: (index: number) => void;
};
function QuestionCard({
  index,
  total,
  question,
  selectedId,
  onAnswer,
  onBack
}: QuestionCardProps) {
  const progress = (index + 1) / total * 100;
  return <div className="her-step-in card-premium champagne-frame px-6 py-9 sm:px-9 sm:py-10" data-id="element-697" data-mp="1c1vfhb14v7pj">
      <div className="flex items-center justify-between" data-id="element-698" data-mp="1q1e9z110ukjd">
        <span className="text-[11px] font-medium uppercase tracking-[0.18em] text-foreground/60" data-id="element-699" data-mp="1q0utzpuxm67z">
          {index + 1} de {total}
        </span>
        {index > 0 && <button type="button" onClick={() => onBack(index)} className="-mr-1 flex items-center gap-1.5 rounded-sm px-1 py-1 text-[12px] tracking-wide text-foreground/60 transition-colors duration-150 ease-out hover:text-gold-light focus:outline-none focus-visible:ring-1 focus-visible:ring-gold" data-id="element-700" data-mp="y9p19uhjjasq">
            <ArrowLeftIcon className="h-3.5 w-3.5" data-id="element-701" data-mp="12ba7iwzkkqty" />
            Volver
          </button>}
      </div>

      <div className="mt-3 h-px w-full bg-gold/12" role="progressbar" aria-valuemin={1} aria-valuemax={total} aria-valuenow={index + 1} aria-label={`Pregunta ${index + 1} de ${total}`} data-id="element-702" data-mp="elpralusw7od">
        <div className="h-px bg-gold transition-[width] duration-300 ease-out" style={{
        width: `${progress}%`
      }} data-id="element-703" data-mp="29pnmrlghxbir" />
      </div>

      {question.connectorLabel && <p className="mt-8 text-[11px] font-medium uppercase tracking-[0.16em] text-foreground/60" data-id="element-704" data-mp="2eme389w7q94p">
          {question.connectorLabel}
        </p>}

      <h2 id="her-quiz-heading" className={`font-display font-medium text-[1.6rem] leading-[1.25] text-foreground sm:text-[1.8rem] ${question.connectorLabel ? 'mt-2' : 'mt-8'}`} data-id="element-705" data-mp="2oejwdmy7awlh">
        {question.prompt}
      </h2>

      <div className="mt-7 flex flex-col gap-2.5" data-id="element-706" data-mp="1vqe8irbuorhf">
        {question.answers.map(answer => {
        const isSelected = answer.id === selectedId;
        return <button key={answer.id} type="button" onClick={() => onAnswer(index, answer)} aria-pressed={isSelected} className={`flex min-h-[52px] w-full items-center justify-between gap-3 rounded-sm border px-4 py-3 text-left text-[15px] leading-snug transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-1 focus-visible:ring-gold ${isSelected ? 'border-gold/70 bg-gold/10 text-foreground' : 'border-gold/12 bg-white/[0.02] text-foreground/90 hover:border-gold/50 hover:text-foreground'}`} data-id="element-707" data-mp="25nsm8j6e3261">
              <span data-id="element-708" data-mp="vp9dj4l2fvmu">{answer.label}</span>
              {isSelected && <CheckIcon className="h-4 w-4 shrink-0 text-gold-light" data-id="element-709" data-mp="vnpz7jiyzunp" />}
            </button>;
      })}
      </div>
    </div>;
}

/* ------------------------------------------------------------------ */
/* RESULT                                                              */
/* ------------------------------------------------------------------ */

type ResultCardProps = {
  result: ResultKind;
  onCta: (result: ResultKind) => void;
  onRetake: () => void;
};

/** Day ranges for each phase within the 21-day protocol — used by the arc lists below. */
const PHASE_DAYS: Record<Phase, string> = {
  H: 'Día 1–7',
  E: 'Día 8–14',
  R: 'Día 15–21'
};

/** Fixed protocol sequence, always shown in this order regardless of which phase is dominant. */
const PHASE_SEQUENCE: Phase[] = ['H', 'E', 'R'];

/* Short, concrete fragments for the dominant phase's highlighted bullet. */
const PHASE_HIGHLIGHTS: string[] = ['Recetas y prácticas específicas', 'Rutinas de acompañamiento'];

/**
 * Single source of truth for the single-phase (H/E/R) personalization sentence.
 * Rendered verbatim in ResultCard AND passed into the quiz_result trackEvent
 * payload, so the UI copy and the analytics payload can never drift apart.
 */
const RESULT_PERSONALIZATION_SENTENCE = 'Por lo que nos contaste, esta es el área que más conecta con lo que hoy estás buscando. Puede ser un buen punto de partida para empezar H.E.R.';

/** Same grounded starting-point framing for the neutral GENERAL outcome. */
const GENERAL_PERSONALIZATION_SENTENCE = 'Por lo que nos contaste, hay varias áreas que se conectan con lo que hoy estás buscando. Por eso, el recorrido completo de 21 días puede ser un buen punto de partida.';
function ResultCard({
  result,
  onCta,
  onRetake
}: ResultCardProps) {
  const {
    name,
    icon: PhaseIcon
  } = RESULT_META[result];
  const isGeneral = result === 'GENERAL';
  const otherPhases = isGeneral ? [] : PHASE_SEQUENCE.filter(p => p !== result);
  return <div className="her-step-in card-premium champagne-frame !border-gold/15 px-6 py-8 sm:px-9 sm:py-10" data-id="element-710" data-mp="3oni4tnzuwl9i">
      {/* TOP OF RESULT — icon + "Tu punto de partida: {name}" is the first thing seen,
          no scrolling needed to get the takeaway. Content-relevance framing, not a diagnosis. */}
      <div className="flex items-center gap-3" data-id="element-711" data-mp="3tkid85k6i03s">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-gold/50 text-gold-light" data-id="element-712" data-mp="3tjqsxqw17ost">
          <PhaseIcon className="h-6 w-6" data-id="element-713" data-mp="67z8v8pg66pi" />
        </span>
        <h2 id="her-quiz-heading" className="font-display font-medium text-[1.55rem] leading-[1.15] text-foreground sm:text-[1.8rem]" data-id="element-714" data-mp="6akdg5h4r0qy">
          Tu punto de partida: {name}
        </h2>
      </div>

      {/* PERSONALIZATION — kept to 2-3 lines max, dynamic-ready per result. */}
      <p className="mt-4 max-w-[38ch] text-[14px] leading-relaxed text-foreground/60 sm:text-[15px]" data-id="element-715" data-mp="nbg5nledo0em">
        {isGeneral ? GENERAL_PERSONALIZATION_SENTENCE : RESULT_PERSONALIZATION_SENTENCE}
      </p>

      {isGeneral ?
    // GENERAL — no phase dominates, so all three get equal visual weight: same
    // border/background treatment, same text size, no gold emphasis anywhere.
    <div className="mt-6 flex flex-col gap-2" data-id="element-716" data-mp="16ym9nv9zgi1w">
          {PHASE_SEQUENCE.map(p => <div key={p} className="flex items-center justify-between rounded-sm border border-gold/12 bg-white/[0.02] px-4 py-3" data-id="element-717" data-mp="1az64gs7339kv">
              <span className="text-[13.5px] text-foreground/90" data-id="element-718" data-mp="1fzu4u4h7d2rr">
                {PHASE_DAYS[p]} · {RESULT_META[p].name}
              </span>
            </div>)}
        </div> : <>
          {/* Compact 21-day arc — highlighted dominant phase + short bullets,
              then a de-emphasized one-liner per remaining phase. No accordion, no long copy. */}
          <div className="mt-6 rounded-sm border border-gold/40 bg-gold/[0.06] px-4 py-4" data-id="element-719" data-mp="3f5hem07d3oqp">
            <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-gold-light" data-id="element-720" data-mp="3aer7ahfdlbtd">
              {PHASE_DAYS[result]} · Fase {name}
            </p>
            <ul className="mt-2.5 space-y-1.5" data-id="element-721" data-mp="35g64ca8qoe18">
              {PHASE_HIGHLIGHTS.map(item => <li key={item} className="flex items-start gap-2 text-[13.5px] leading-snug text-foreground/90" data-id="element-722" data-mp="30hmj6c1yz69x">
                  <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-gold" aria-hidden="true" data-id="element-723" data-mp="1xmjhsse4agg" />
                  <span data-id="element-724" data-mp="3t5s3wyt0wv9r">{item}</span>
                </li>)}
            </ul>
          </div>

          <div className="mt-3 flex flex-col gap-1" data-id="element-725" data-mp="3o4unaqnnw9ad">
            {otherPhases.map(p => <p key={p} className="text-[12.5px] text-foreground/60" data-id="element-726" data-mp="3j7ks0udld3em">
                → Luego, {RESULT_META[p].name} ({PHASE_DAYS[p]})
              </p>)}
          </div>
        </>}

      {/* CTA sits close to the list above — no long scroll between takeaway and action. */}
      <button type="button" onClick={() => onCta(result)} className="mt-5 flex min-h-[56px] w-full items-center justify-center gap-2.5 rounded-sm bg-gold px-6 text-[14px] font-semibold uppercase tracking-[0.14em] text-[#0a0807] transition-colors duration-200 ease-out hover:bg-gold-light focus:outline-none focus-visible:ring-1 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-background" data-id="element-727" data-mp="2xg1hgrn6j4nd">
        <SparklesIcon className="h-4 w-4" aria-hidden="true" data-id="element-728" data-mp="3g612n3fwfzli" />
        Quiero empezar H.E.R.
      </button>

      <button type="button" onClick={onRetake} className="mx-auto mt-4 block rounded-sm px-2 py-1 text-[12px] tracking-wide text-foreground/60 underline decoration-gold/20 underline-offset-4 transition-colors duration-150 ease-out hover:text-gold-light focus:outline-none focus-visible:ring-1 focus-visible:ring-gold" data-id="element-729" data-mp="3l5vt55vrin61">
        Volver a hacer el test
      </button>
    </div>;
}